/**
 * Sentry initialization and configuration
 *
 * DSN and environment can be provided via:
 * 1. window.runtimeConfig (Helm/runtime injection)
 * 2. import.meta.env (Vite env variables)
 * 3. widget mount config (B2BDemo.mount() options)
 *
 * NOTE: DSN is NOT read from URL parameters or iframe data-attributes for security reasons.
 *
 * Graceful degradation: if DSN is not provided, Sentry is not initialized.
 *
 * Environments:
 * - production: Production server (demo.shiftgears.ai)
 * - staging/test: Test server
 * - development: Local development (errors not sent)
 *
 * Embed types (sent as tag):
 * - standalone: Regular website
 * - iframe: Embedded in iframe
 * - widget: Embedded via B2BDemo.mount() SPA script
 */

import * as Sentry from '@sentry/react';
import pkg from '../../package.json';

// Track initialization state
let isInitialized = false;

/**
 * Detect embed type based on context
 * @returns {'standalone' | 'iframe' | 'widget'}
 */
function detectEmbedType() {
  if (typeof window === 'undefined') return 'standalone';

  // Widget mode: B2BDemo.mount() was called
  if (window.runtimeConfig?.VITE_ENVIRONMENT_NAME === 'widget') {
    return 'widget';
  }

  // Iframe mode: running inside an iframe
  try {
    if (window.self !== window.top) {
      return 'iframe';
    }
  } catch {
    // Cross-origin iframe - считаем iframe
    return 'iframe';
  }

  return 'standalone';
}

/**
 * Check if hostname is local development
 * @param {string} hostname
 * @returns {boolean}
 */
function isLocalhost(hostname) {
  return hostname === 'localhost'
    || hostname === '127.0.0.1'
    || hostname === '0.0.0.0'
    || hostname === '::1'
    || hostname.endsWith('.local');
}

/**
 * Get Sentry configuration from various sources
 * Priority: widget config > runtimeConfig > import.meta.env
 *
 * @param {Object} overrides - Configuration overrides (from widget mount)
 * @returns {Object} Sentry configuration
 */
export function getSentryConfig(overrides = {}) {
  const runtime = (typeof window !== 'undefined' && window.runtimeConfig) || {};

  return {
    dsn: overrides.sentryDsn
      || runtime.VITE_SENTRY_DSN
      || import.meta.env.VITE_SENTRY_DSN
      || null,
    environment: overrides.sentryEnvironment
      || runtime.VITE_SENTRY_ENVIRONMENT
      || import.meta.env.VITE_SENTRY_ENVIRONMENT
      || runtime.VITE_ENVIRONMENT_NAME
      || import.meta.env.VITE_ENVIRONMENT_NAME
      || 'development',
    release: overrides.sentryRelease
      || runtime.VITE_SENTRY_RELEASE
      || import.meta.env.VITE_SENTRY_RELEASE
      || `b2b-demo@${pkg.version}`,
    embedType: overrides.embedType || detectEmbedType(),
    // Organization and inventory for filtering errors by dealer
    organizationId: overrides.organizationId
      || runtime.VITE_ORGANIZATION_ID
      || import.meta.env.VITE_ORGANIZATION_ID
      || null,
    inventorySlug: overrides.inventorySlug
      || runtime.VITE_INVENTORY_SLUG
      || import.meta.env.VITE_INVENTORY_SLUG
      || null,
  };
}

/**
 * Initialize Sentry SDK
 *
 * @param {Object} overrides - Configuration overrides
 * @returns {boolean} True if Sentry was initialized, false otherwise
 */
export function initSentry(overrides = {}) {
  // Prevent double initialization
  if (isInitialized) {
    console.log('[Sentry] Already initialized, skipping');
    return true;
  }

  const config = getSentryConfig(overrides);

  // Graceful degradation: don't initialize without DSN
  if (!config.dsn) {
    console.log('[Sentry] No DSN provided, Sentry will not be initialized');
    return false;
  }

  // Don't send errors from local development
  if (typeof window !== 'undefined' && isLocalhost(window.location.hostname)) {
    console.log('[Sentry] Local development detected, Sentry will not be initialized');
    return false;
  }

  try {
    // Determine sample rates based on environment and embed type
    const isProduction = config.environment === 'production';
    const isWidget = config.embedType === 'widget';

    const tracesSampleRate = isProduction ? 0.1 : 1.0;

    // Disable session replay for widget mode — third-party tracking without
    // explicit consent triggers Google Ads "Compromised Site" flags
    const replaysSessionSampleRate = isWidget ? 0 : (isProduction ? 0.1 : 0);
    const replaysOnErrorSampleRate = isWidget ? 0 : (isProduction ? 1.0 : 0);

    // Disable trace propagation headers for widget mode — injecting tracing
    // headers into cross-origin requests from a third-party script looks
    // suspicious to automated security scanners
    const tracePropagationTargets = isWidget
      ? []
      : ['localhost', /^https:\/\/.*\.shiftgears\.ai/];

    // Skip replay integration entirely in widget mode. The build-time check
    // comes first so the widget bundle (VITE_ENVIRONMENT_NAME is 'widget' there)
    // drops this branch outright: replay can't switch on there even if the
    // embed type is misdetected, and vite.config.widget.js swaps the replay
    // package itself for a stub (#1469).
    const integrations = [Sentry.browserTracingIntegration()];
    if (import.meta.env.VITE_ENVIRONMENT_NAME !== 'widget' && !isWidget) {
      integrations.push(
        Sentry.replayIntegration({
          // Маскируем все текстовое содержимое для приватности
          maskAllText: true,
          // Блокируем все медиа
          blockAllMedia: true,
        }),
      );
    }

    Sentry.init({
      dsn: config.dsn,
      environment: config.environment,
      release: config.release,

      // Performance monitoring
      tracesSampleRate,

      // Distributed tracing targets
      tracePropagationTargets,

      // Session replay
      replaysSessionSampleRate,
      replaysOnErrorSampleRate,

      // Integrations
      integrations,

      // Initial tags for filtering in Sentry
      initialScope: {
        tags: {
          embed_type: config.embedType,
          organization_id: config.organizationId || 'unknown',
          inventory_slug: config.inventorySlug || 'unknown',
        },
      },

      // Filter out common non-actionable errors
      ignoreErrors: [
        // Browser extensions
        'top.GLOBALS',
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        'http://tt.teletrader',
        'jigsaw is not defined',
        'ComboSearch is not defined',
        'http://loading.retry.widdit.com/',
        'atomicFindClose',

        // Network errors that users can't fix
        'Failed to fetch',
        'NetworkError',
        'Network request failed',
        'Load failed',
        'net::ERR_',

        // AbortController cancellations
        'AbortError',
        'The operation was aborted',

        // Common React/browser errors that are usually benign
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        'Non-Error promise rejection captured',
      ],

      // Don't send errors from local development (backup check)
      beforeSend(event) {
        if (typeof window !== 'undefined' && isLocalhost(window.location.hostname)) {
          return null;
        }
        return event;
      },
    });

    isInitialized = true;
    console.log('[Sentry] Initialized successfully', {
      environment: config.environment,
      release: config.release,
      embedType: config.embedType,
      organizationId: config.organizationId,
      inventorySlug: config.inventorySlug,
    });

    return true;
  } catch (error) {
    console.error('[Sentry] Failed to initialize:', error);
    return false;
  }
}

/**
 * Check if Sentry is initialized
 * @returns {boolean}
 */
export function isSentryInitialized() {
  return isInitialized;
}

/**
 * Capture exception manually
 * @param {unknown} error - Error to capture (will be normalized to Error object)
 * @param {Object} context - Additional context
 */
export function captureException(error, context = {}) {
  if (!isInitialized) {
    console.error('[Sentry] Not initialized, error not captured:', error);
    return;
  }

  // Ensure we're capturing an Error object
  const errorToCapture = error instanceof Error
    ? error
    : new Error(String(error));

  Sentry.captureException(errorToCapture, {
    extra: {
      ...context,
      // Сохраняем оригинальное значение если это не Error
      originalError: error instanceof Error ? undefined : error,
    },
  });
}

/**
 * Set user context for better error tracking
 * @param {Object} user - User information
 */
export function setUser(user) {
  if (!isInitialized) return;

  Sentry.setUser(user);
}

/**
 * Add breadcrumb for debugging
 * @param {Object} breadcrumb
 */
export function addBreadcrumb(breadcrumb) {
  if (!isInitialized) return;

  Sentry.addBreadcrumb(breadcrumb);
}

/**
 * Set tag for filtering errors
 * @param {string} key
 * @param {string} value
 */
export function setTag(key, value) {
  if (!isInitialized) return;

  Sentry.setTag(key, value);
}

// Export Sentry for direct access (e.g., ErrorBoundary)
export { Sentry };
